import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, message, Typography } from "@mimber/ui-web"
import {
  confirmEmailSchema,
  mapCognitoError,
  confirmSignUpCode,
  resendSignUpCode,
  autoSignIn,
} from "@mimber/auth"
import type {
  ConfirmEmailFormProps,
  ConfirmEmailFormData,
} from "./ConfirmEmailForm.types"

const { Paragraph } = Typography

export const ConfirmEmailForm: React.FunctionComponent<
  ConfirmEmailFormProps
> = ({ email, onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const form = useForm<ConfirmEmailFormData>({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      code: "",
    },
  })

  const handleSubmit = async (data: ConfirmEmailFormData) => {
    setIsLoading(true)
    try {
      const result = await confirmSignUpCode(email, data.code)
      message.success("Email verified successfully!")

      // Auto sign in if available
      if (result.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        await autoSignIn()
      }

      onSuccess?.()
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
      onError?.(error as Error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setIsResending(true)
    try {
      await resendSignUpCode(email)
      message.success("Verification code resent! Check your email.")
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
    } finally {
      setIsResending(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Paragraph>
          We sent a verification code to <strong>{email}</strong>
        </Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="code"
          type="text"
          label="Verification Code"
          placeholder="Enter 6-digit code"
          maxLength={6}
          required
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full mb-3"
        >
          Verify Email
        </Button>
      </Form>

      <div className="text-center">
        <Button type="link" onClick={handleResend} loading={isResending}>
          Didn't receive code? Resend
        </Button>
      </div>
    </>
  )
}
